import React, { FC, useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import { createUseStyles } from 'react-jss'
import { deleteNote, getMemberNotes } from '../../../api/notes'
import { Member } from '../../../models/Member'
import { Note, mapNotes } from '../../../models/Note'
import { Workplace } from '../../../models/Workplace'
import { colors } from '../../../theme/palette'
import { baseButton } from '../../../theme/reuseables'
import { Circle } from '../../ui/Card'
import { CircleIcon } from '../../ui/Circle/Circle'
import { NoteButton } from '../../ui/NoteButton'
import { Overlay } from '../../ui/Sidebar'
import { DrawerHeaderWorkplaceInfo } from '../FormElements/DrawerHeader'
import { AddNewNote } from './AddNewNote'
import { EditMemberNoteButton } from './EditMemberNoteButton'
import { EditNote } from './EditNote'
import { NoteOverviewSidebar } from './NoteOverviewSidebar'
import { Note as NoteIcon, Plus } from '../../../assets/icons'
import { Button } from '../../ui/Button/Button'
import breakpoints from '../../../theme/breakpoints'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'

import { profileAtom } from '../../../api/profile/profile'

type ViewStates = 'default' | 'create' | 'edit' | 'delete'

interface EditWorkplaceNotesProps {
    workplace: Workplace
}

const useStyles = createUseStyles({
    button: {
        ...baseButton,
        ...{
            backgroundColor: colors.white,
            fontSize: '16px',
            padding: '16px',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            textAlign: 'left',
            justifyContent: 'start',
        },
    },
    link: {
        fontWeight: 400,
        fontSize: '16px',
        textDecoration: 'underline',
    },
    buttons: {
        display: 'flex',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                // justifyContent: 'start',
            },
        },
    },
    addNote: {
        position: 'relative',
        top: '6px',
    },
    noteButton: {
        position: 'relative',
        top: '4px',
    },
})

const EditWorkplaceNotes: FC<EditWorkplaceNotesProps> = ({ workplace }) => {
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const [notes, setNotes] = useState([])
    const [isOpen, setIsOpen] = useState(false)
    const [viewState, setViewState] = useState<ViewStates>('default')
    const [loading, setLoading] = useState(true)
    const [editNote, setEditNote] = useState<Note | undefined>(undefined)
    const classes = useStyles()

    const init = (): void => {
        async function fetchData(): Promise<void> {
            const response = await getMemberNotes(
                workplace.number,
                'ArbejdsstedNr'
            )
            const notesMapped = mapNotes(response.Noter)

            setNotes(notesMapped)

            setLoading(false)
        }

        fetchData()
    }

    const onClickDelete = async (note: Note): Promise<void> => {
        setViewState('default')
        const response = await deleteNote(note.id)
        if (response) {
            setViewState('default')
            if (notes.length === 1) {
                setIsOpen(false)
            }
            init()
        }
    }

    useEffect(() => {
        if (isOpen) {
            init()
        }
    }, [isOpen])

    // if (loading) return null

    return (
        <>
            <div className={classes.buttons}>
                <Button
                    type="action"
                    style={{
                        fontWeight: 400,
                        fontSize: '16px',
                        textDecoration: 'underline',
                    }}
                    onClick={() => {
                        if (!authProfile.isImitator) {
                            setIsOpen(true)
                            if (workplace.numberOfNotes > 0) {
                                setViewState('default')
                            } else {
                                setViewState('create')
                            }
                        } else {
                            setShowImitatorModal(true)
                        }
                    }}
                >
                    {workplace.numberOfNotes > 0 ? 'Vis noter' : 'Tilføj note'}
                </Button>
                <div className={classes.noteButton}>
                    <NoteButton
                        onClick={() => {
                            if (authProfile.isImitator) {
                                setShowImitatorModal(true)
                            } else if (notes.length > 0) {
                                setIsOpen(true)
                                setViewState('default')
                            }
                        }}
                    >
                        {workplace.numberOfNotes}
                    </NoteButton>
                </div>

                <button
                    className={classes.button}
                    style={{
                        width: 'auto',
                        padding: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    type="button"
                    onClick={() => {
                        if (authProfile.isImitator) {
                            setShowImitatorModal(true)
                        } else {
                            setIsOpen(true)
                            setViewState('create')
                        }
                    }}
                >
                    <div className={classes.addNote}>
                        <Plus width="26px" color={colors.green500} />
                    </div>
                </button>
            </div>

            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                }}
            >
                <DrawerHeaderWorkplaceInfo title={workplace.name} />

                {viewState === 'create' ? (
                    <AddNewNote
                        workPlace={workplace}
                        hasNotes={workplace.numberOfNotes > 0}
                        type="workplace"
                        onClickCancel={() => {
                            setViewState('default')
                            if (notes.length === 0) {
                                setIsOpen(false)
                            }
                        }}
                        onUpdated={() => {
                            setViewState('default')
                            init()
                        }}
                    />
                ) : null}

                {viewState === 'edit' && editNote ? (
                    <EditNote
                        note={editNote}
                        onCancel={() => setViewState('default')}
                        onUpdated={() => {
                            init()
                            setViewState('default')
                        }}
                    />
                ) : null}

                {viewState === 'default' ? (
                    <NoteOverviewSidebar
                        notes={notes}
                        onClickEdit={(note) => {
                            setIsOpen(true)
                            setEditNote(note)
                            setViewState('edit')
                        }}
                        onClickDelete={onClickDelete}
                        onClickCreate={() => setViewState('create')}
                    />
                ) : null}
            </Overlay>
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </>
    )
}

export { EditWorkplaceNotes }
